import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  FaBuilding,
  FaFilter,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaSpinner,
  FaExclamationTriangle,
} from "react-icons/fa";
import { IoMdRefresh } from "react-icons/io";
import APi from "../../api";
import dayjs from "dayjs";
import DataAset from "../../components/invaset/DataAset";
import { SipDropdown, SipSearch, withAll } from "../../components/form/SipControls";

function InventarisAset() {
  const navigate = useNavigate();

  // State Management
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalItems, setTotalItems] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  // Filter States
  const [filters, setFilters] = useState({
    search: "",
    kategori: "",
    gedung: "",
    tahun_pembelian: "",
    sumber_dana: "",
    merk: "",
  });

  // Pagination States
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
  });

  // Debounced search
  const [searchTerm, setSearchTerm] = useState("");

  // Filter options (akan diisi dari API)
  const [filterOptions, setFilterOptions] = useState({
    kategori: [],
    gedung: [],
    tahun_pembelian: [],
    sumber_dana: [],
    merk: [],
  });

  // Debounce hook untuk search
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchTerm }));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch data dengan filter dan pagination
  const fetchData = useCallback(
    async (resetPage = false) => {
      try {
        setLoading(true);
        setError(null);

        const currentPage = resetPage ? 1 : pagination.page;

        const params = new URLSearchParams({
          page: currentPage.toString(),
          limit: pagination.limit.toString(),
          ...Object.fromEntries(
            Object.entries(filters).filter(([_, value]) => value !== "")
          ),
        });

        const response = await APi.get(`/mon/inventaris-aset?${params}`);

        if (response.data.success) {
          setData(response.data.data);
          setTotalItems(response.data.total || 0);
          setPagination((prev) => ({
            ...prev,
            page: currentPage,
            totalPages: Math.ceil((response.data.total || 0) / prev.limit),
          }));
        } else {
          throw new Error(response.data.message || "Gagal mengambil data");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(error.message || "Terjadi kesalahan saat mengambil data");
        setData([]);
      } finally {
        setLoading(false);
      }
    },
    [filters, pagination.page, pagination.limit]
  );

  // Fetch filter options
  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await APi.get("/mon/inventaris-aset/filter-options");
      if (response.data.success) {
        setFilterOptions(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  // Fetch data when filters change
  useEffect(() => {
    fetchData(true);
  }, [filters]);

  // Fetch data when page changes
  useEffect(() => {
    if (pagination.page > 1) {
      fetchData(false);
    }
  }, [pagination.page]);

  // Handle filter change
  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters({
      search: "",
      kategori: "",
      gedung: "",
      tahun_pembelian: "",
      sumber_dana: "",
      merk: "",
    });
    setSearchTerm("");
  }, []);

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  }, []);

  // Handle back navigation
  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Export data

  // Memoized filter count
  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter((value) => value !== "").length;
  }, [filters]);

  return (
    <div className="space-y-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* ==================== HEADER SECTION ==================== */}
        <motion.div
          className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-primary px-3 py-4 md:px-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="text-white">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                    <FaBuilding className="text-xl" />
                  </div>
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold">
                      Inventaris Aset
                    </h1>
                    <p className="text-white/80 text-sm mt-1">
                      Kelola dan pantau aset organisasi
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white border border-white/20 px-4 py-2 rounded-xl hover:bg-white/20 transition-all duration-300"
                >
                  <FaChevronLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Kembali</span>
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ==================== SEARCH AND FILTER SECTION ==================== */}
        <motion.div
          className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <div className="space-y-4">
            {/* Search Bar dan Filter Toggle */}
            <div className="flex flex-col gap-4 xl:flex-row">
              <div className="min-w-0 flex-1">
                <SipSearch
                  placeholder="Cari berdasarkan kode, deskripsi, merk, atau spesifikasi..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="flex w-full flex-wrap gap-2 xl:w-auto">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl border transition-all duration-200 ${
                    showFilters
                      ? "bg-blue-50 border-blue-200 text-blue-700"
                      : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <FaFilter className="w-4 h-4" />
                  <span>Filter</span>
                  {activeFilterCount > 0 && (
                    <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                      {activeFilterCount}
                    </span>
                  )}
                </button>

                <button
                  onClick={() => fetchData(true)}
                  disabled={loading}
                  className="flex items-center justify-center gap-2 bg-primary text-white px-6 py-3 rounded-xl hover:bg-primary/90 transition-all duration-200 shadow-md hover:shadow-lg font-medium"
                >
                  <IoMdRefresh
                    className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                  />
                  <span className="hidden sm:inline">Refresh</span>
                </button>
              </div>
            </div>

            {/* Advanced Filters */}
            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="border-t border-gray-200 pt-4"
                >
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Kategori
                      </label>
                      <SipDropdown
                        value={filters.kategori}
                        onChange={(e) => handleFilterChange("kategori", e.target.value)}
                        placeholder="Semua Kategori"
                        options={withAll(filterOptions.kategori, "Semua Kategori")}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Gedung/Ruang
                      </label>
                      <SipDropdown
                        value={filters.gedung}
                        onChange={(e) => handleFilterChange("gedung", e.target.value)}
                        placeholder="Semua Gedung"
                        options={withAll(filterOptions.gedung, "Semua Gedung")}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tahun Pembelian
                      </label>
                      <SipDropdown
                        value={filters.tahun_pembelian}
                        onChange={(e) => handleFilterChange("tahun_pembelian", e.target.value)}
                        placeholder="Semua Tahun"
                        options={withAll(filterOptions.tahun_pembelian, "Semua Tahun")}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sumber Dana
                      </label>
                      <SipDropdown
                        value={filters.sumber_dana}
                        onChange={(e) => handleFilterChange("sumber_dana", e.target.value)}
                        placeholder="Semua Sumber"
                        options={withAll(filterOptions.sumber_dana, "Semua Sumber")}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Merk
                      </label>
                      <SipDropdown
                        value={filters.merk}
                        onChange={(e) => handleFilterChange("merk", e.target.value)}
                        placeholder="Semua Merk"
                        options={withAll(filterOptions.merk, "Semua Merk")}
                      />
                    </div>

                    <div className="flex items-end">
                      <button
                        onClick={clearFilters}
                        className="flex items-center gap-2 px-4 py-2 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-all duration-200"
                      >
                        <FaTimes className="w-4 h-4" />
                        Reset Filter
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* ==================== TABLE SECTION ==================== */}
        <motion.div
          className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {/* Table Header dengan Info */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Data Inventaris Aset
                </h3>
                <p className="text-sm text-gray-600">
                  Menampilkan {data.length} dari {totalItems} total aset
                </p>
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>
                  Halaman {pagination.page} dari {pagination.totalPages}
                </span>
              </div>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center gap-3 text-blue-600">
                <FaSpinner className="w-5 h-5 animate-spin" />
                <span>Memuat data...</span>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center gap-3 text-red-600">
                <FaExclamationTriangle className="w-5 h-5" />
                <span>{error}</span>
              </div>
            </div>
          )}

          {/* Table Content */}
          {!loading && !error && (
            <>
              <div className="overflow-x-auto">
                <DataAset data={data} />
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Menampilkan {(pagination.page - 1) * pagination.limit + 1}{" "}
                      -{" "}
                      {Math.min(pagination.page * pagination.limit, totalItems)}{" "}
                      dari {totalItems} hasil
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1}
                        className="flex items-center gap-1 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      >
                        <FaChevronLeft className="w-3 h-3" />
                        Sebelumnya
                      </button>

                      <div className="flex items-center gap-1">
                        {Array.from(
                          { length: Math.min(5, pagination.totalPages) },
                          (_, i) => {
                            const pageNum = i + 1;
                            return (
                              <button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                className={`px-3 py-2 text-sm rounded-lg transition-all duration-200 ${
                                  pagination.page === pageNum
                                    ? "bg-blue-600 text-white"
                                    : "border border-gray-200 hover:bg-gray-100"
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          }
                        )}
                      </div>

                      <button
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page === pagination.totalPages}
                        className="flex items-center gap-1 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      >
                        Selanjutnya
                        <FaChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}

export default InventarisAset;
